import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

const SEO = ({
  title,
  description,
  keywords,
  image = '/images/logo.svg',
  type = 'website',
  noindex = false,
  nofollow = false,
}) => {
  const { t, currentLanguage } = useLanguage();
  const location = useLocation();
  
  // Get base URL from environment or use default
  const baseUrl = process.env.REACT_APP_BASE_URL || 'https://www.terminteiler.com';
  const siteName = t('seo.siteName') || 'TerminTeiler';
  
  // Construct full URL
  const fullUrl = `${baseUrl}${location.pathname}`;
  
  // Construct full image URL
  const fullImageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`;
  
  // Default title and description from translations
  const defaultTitle = t('seo.defaultTitle') || 'TerminTeiler';
  const defaultDescription = t('seo.defaultDescription') || 'Share doctor appointments and reduce waiting times';
  
  // Use provided title/description or fallback to defaults
  const pageTitle = title || defaultTitle;
  const pageDescription = description || defaultDescription;
  
  // Construct full title (page title | site name)
  const fullTitle = title ? `${title} | ${siteName}` : defaultTitle;
  
  // Robots meta
  const robotsContent = [];
  if (noindex) robotsContent.push('noindex');
  if (nofollow) robotsContent.push('nofollow');
  if (robotsContent.length === 0) robotsContent.push('index', 'follow');
  
  // Alternate language URLs
  const alternateUrls = {
    de: `${baseUrl}${location.pathname}`,
    en: `${baseUrl}${location.pathname}`,
  };
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={pageDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robotsContent.join(', ')} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Language Alternates */}
      <link rel="alternate" hreflang="de" href={alternateUrls.de} />
      <link rel="alternate" hreflang="en" href={alternateUrls.en} />
      <link rel="alternate" hreflang="x-default" href={alternateUrls.de} />
      
      {/* Open Graph Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={currentLanguage === 'de' ? 'de_DE' : 'en_US'} />
      <meta property="og:locale:alternate" content={currentLanguage === 'de' ? 'en_US' : 'de_DE'} />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={fullImageUrl} />
    </Helmet>
  );
};

export default SEO;
